"use client";
// 2026-09-26 saludo por hora + typewriter (research 2026-09-25 vía TinyFish:
// patrón temporal de la competencia — el home saluda según la hora del día:
// mañana/tarde/noche — en vez de un título fijo).
// Mañana 5–11, tarde 12–19, noche resto (hora local del navegador).

import { useEffect, useState } from "react";

export type GreetingKey =
  | "chatHeroGreetingMorning"
  | "chatHeroGreetingAfternoon"
  | "chatHeroGreetingEvening"
  | "chatHeroGreetingMorningAlt"
  | "chatHeroGreetingAfternoonAlt"
  | "chatHeroGreetingEveningAlt";

export function greetingKeyForHour(hour: number): GreetingKey {
  const h = Number.isFinite(hour) ? Math.floor(hour) : 12;
  if (h >= 5 && h < 12) return "chatHeroGreetingMorning";
  if (h >= 12 && h < 20) return "chatHeroGreetingAfternoon";
  return "chatHeroGreetingEvening";
}

// 2026-10-04 dueño (research 2026 industry pattern: 3-5 variantes por franja que
// rotan): base vs Alt por paridad del día del mes — determinista (sin
// Math.random: SSR-safe, sin hydration mismatch), varía entre días.
export function greetingAltKeyForHour(hour: number): GreetingKey {
  const h = Number.isFinite(hour) ? Math.floor(hour) : 12;
  if (h >= 5 && h < 12) return "chatHeroGreetingMorningAlt";
  if (h >= 12 && h < 20) return "chatHeroGreetingAfternoonAlt";
  return "chatHeroGreetingEveningAlt";
}

export function greetingKeyForDate(date: Date): GreetingKey {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) {
    return "chatHeroGreetingMorning";
  }
  const hour = date.getHours();
  // Días pares = variante Alt, impares = base (rotación industry pattern).
  if (date.getDate() % 2 === 0) return greetingAltKeyForHour(hour);
  return greetingKeyForHour(hour);
}

// 2026-10-04 saludo FIJO (sin rotación): el texto se elige una sola vez por
// montaje vía greetingKeyForDate (día par Alt / impar base). Sin intervalo,
// sin fade loop — el typewriter de ChatGreeting corre una sola vez y respeta
// prefers-reduced-motion.

// Ritmo fluido y suave de tecleado en streaming (2026 update):
// Avance rápido y natural por palabras (~32ms base + ligero aporte por longitud),
// pausas breves y orgánicas en puntuación (35-50ms) sin congelamiento ni retraso tosco.
function delayForUnit(unit: string): number {
  const trimmed = unit.trimEnd();
  const base = 32 + Math.min(unit.length * 3, 20);
  if (/[,;:…]$/.test(trimmed)) return base + 35;
  if (/[?!.¡!¿?…—]$/.test(trimmed)) return base + 50;
  return base;
}

interface WordSegmentLike {
  segment: string;
  isWordLike?: boolean;
}

function segmentWords(text: string): WordSegmentLike[] | null {
  try {
    const Seg = (
      Intl as unknown as {
        Segmenter?: new (
          locale: string | undefined,
          options?: { granularity?: string },
        ) => { segment(input: string): Iterable<WordSegmentLike> };
      }
    ).Segmenter;
    if (typeof Seg !== "function") return null;
    return Array.from(
      new Seg(undefined, { granularity: "word" }).segment(text),
    );
  } catch {
    return null;
  }
}

/**
 * 2026-10-03 RUN12 FASE2 F1+F2+C7: unidades de tecleado del saludo — cada
 * palabra con sus separadores siguientes en una unidad (["Good ", "morning ",
 * "— ", …]). Sin Segmenter: split por espacios conservando separadores.
 * Sin espacios y sin Segmenter: el texto íntegro (1 unidad, sin cortes).
 */
export function splitTypewriterUnits(text: string): string[] {
  if (text.length === 0) return [];
  const segs = segmentWords(text);
  if (segs && segs.length > 0) {
    const units: string[] = [];
    let buf = "";
    let hasWord = false;
    for (const s of segs) {
      buf += s.segment;
      if (s.isWordLike) hasWord = true;
      else if (hasWord) {
        units.push(buf);
        buf = "";
        hasWord = false;
      }
    }
    if (buf) units.push(buf);
    return units.length > 0 ? units : [text];
  }
  const parts = text.match(/\S+\s*|\s+/g);
  return parts && parts.length > 0 ? parts : [text];
}

export interface AccentSplit {
  /** Tramo en foreground (incluye el espacio/trailing de corte). */
  plain: string;
  /** Última palabra con acento ("" = sin acento). */
  accent: string;
  /** Offset donde empieza el acento (-1 = sin acento). */
  accentStart: number;
}

/**
 * 2026-10-03 RUN12 FASE2 F1+F2+C7: corte del acento "última palabra"
 * (paridad hero landing heroTitleB). Con espacios: tras el último (sin cambio
 * visual). Sin espacios (zh/ja/lo): SIN acento a propósito — la segmentación
 * CJK termina en partículas/cópulas (です/か/?) donde el color se lee como
 * error, no como jerarquía; el h2 ya da jerarquía por peso (semibold step-5).
 */
export function splitAccentLastWord(text: string): AccentSplit {
  const spaceIdx = text.lastIndexOf(" ");
  if (spaceIdx > 0 && spaceIdx < text.length - 1) {
    return {
      plain: text.slice(0, spaceIdx + 1),
      accent: text.slice(spaceIdx + 1),
      accentStart: spaceIdx + 1,
    };
  }
  return { plain: text, accent: "", accentStart: -1 };
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function")
    return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function ChatGreeting({ text }: { text: string }) {
  // 2026-09-26 a11y + CPU budget: con reduced-motion se muestra completo
  // (sin timer); la cadena de timeouts vive solo durante el tecleado y se
  // limpia al desmontar o al completar (1 timer de vida corta, sin setState en
  // oculta más allá del ~1s inicial). El h2 lleva el texto íntegro en
  // aria-label y el tramo animado va aria-hidden para no spamear lectores.
  // Sin setState síncrono en effects (react-hooks/set-state-in-effect): el
  // estado inicial cubre reduced-motion/vacío y el reset por texto nuevo lo
  // hace el padre con key={text} (remount).
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  // 2026-10-03 RUN12 FASE2 F1+F2+C7: el progreso cuenta UNIDADES-palabra, no
  // unidades UTF-16 (el padre re-monta con key={text} ante texto nuevo).
  const units = splitTypewriterUnits(text);
  const unitCount = units.length;
  const [shownUnits, setShownUnits] = useState(() =>
    prefersReducedMotion() || unitCount === 0 ? unitCount : 0,
  );
  const done = reducedMotion || shownUnits >= unitCount;
  // 2026-10-03: el caret respira 350ms sobre el saludo completo antes de irse
  // (desaparecer en el mismo frame del último carácter se leía como corte).
  const [caretGone, setCaretGone] = useState(false);
  useEffect(() => {
    if (!done || reducedMotion || caretGone) return;
    const id = window.setTimeout(() => setCaretGone(true), 350);
    return () => window.clearTimeout(id);
  }, [done, reducedMotion, caretGone]);
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => {
      setReducedMotion(e.matches);
      if (e.matches) setShownUnits(unitCount);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [text, unitCount]);
  useEffect(() => {
    if (reducedMotion || unitCount === 0) return;
    // 2026-09-26 CPU budget SEED e8851c1e (research 2026 free-tier polling
    // ceilings: skip cuando document.hidden) + 2026-10-03 ritmo humano: cadena
    // de timeouts con delayForUnit por palabra (pausa en puntuación) en vez de
    // intervalo fijo. 1 timer vivo a la vez, vida total ~1s; en pestaña oculta
    // se reintenta sin avanzar (sin wakeups de setState).
    // `units` es del primer render (el padre re-monta con key={text}).
    // Se recomputa dentro del efecto (pura y barata) para no depender de la
    // identidad del array entre renders.
    const localUnits = splitTypewriterUnits(text);
    const localCount = localUnits.length;
    if (localCount === 0) return;
    let timeout: number | undefined;
    let alive = true;
    const step = (index: number) => {
      if (!alive || index >= localCount) return;
      timeout = window.setTimeout(
        () => {
          if (!alive) return;
          if (document.hidden) {
            step(index);
            return;
          }
          setShownUnits(index + 1);
          step(index + 1);
        },
        index === 0 ? 45 : delayForUnit(localUnits[index] ?? ""),
      );
    };
    step(0);
    return () => {
      alive = false;
      if (timeout !== undefined) window.clearTimeout(timeout);
    };
  }, [text, reducedMotion, unitCount]);
  // 2026-10-02 dueño: el acento de color solo en la ÚLTIMA palabra (paridad
  // hero landing: heroTitleB), el resto del saludo en foreground. Antes el
  // .text-chroma-flow cubría toda la frase → contorno/alias raro y sin jerarquía.
  // 2026-10-03 RUN12 FASE2 F1+F2+C7: corte vía splitAccentLastWord (misma
  // regla en el hero privado); dir="auto" para que el tramo animado tome la
  // dirección de su primer carácter fuerte en ar (sin espejar el layout).
  const { accentStart } = splitAccentLastWord(text);
  // 2026-10-03 reveal con vida: cada palabra ya mostrada monta su propio span
  // con animate-greeting-word (rise + fade, solo transform/opacity). Los offsets
  // son prefijos contiguos del texto: el corte cae en frontera de palabra
  // (shaping árabe intacto) y el acento de la última palabra conserva su span
  // text-chroma-flow. En done/reduced-motion se renderiza el texto plano
  // (mismo output que antes: tests de a11y intactos).
  const showCaret = !reducedMotion && !caretGone;
  if (done) {
    const plainShown = accentStart > 0 ? text.slice(0, accentStart) : text;
    const accentShown = accentStart > 0 ? text.slice(accentStart) : "";
    return (
      <span aria-hidden="true" dir="auto">
        {plainShown}
        {accentShown && <span className="text-chroma-flow">{accentShown}</span>}
        {showCaret && <span className="greeting-caret" aria-hidden="true" />}
      </span>
    );
  }
  const shown = units.slice(0, shownUnits);
  // Offsets como prefijos contiguos (sin mutación: O(n²) con n ≤ ~10).
  return (
    <span aria-hidden="true" dir="auto">
      {shown.map((u, i) => {
        const start = shown.slice(0, i).join("").length;
        if (accentStart > 0 && start + u.length > accentStart) {
          const cut = Math.max(accentStart - start, 0);
          const head = u.slice(0, cut);
          const tail = u.slice(cut);
          return (
            <span
              key={`word-accent-${i}-${u}`}
              className="animate-greeting-word"
            >
              {head}
              {tail && <span className="text-chroma-flow">{tail}</span>}
            </span>
          );
        }
        return (
          <span key={`word-${i}-${u}`} className="animate-greeting-word">
            {u}
          </span>
        );
      })}
      {showCaret && <span className="greeting-caret" aria-hidden="true" />}
    </span>
  );
}
